import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { FaMicrophone, FaStop, FaPause, FaPlay, FaUpload, FaTrash, FaDownload, FaTimes, BsPauseFill, BsPlayFill } from '../../../../icons';
import { useWavesurfer } from '@wavesurfer/react';
import { motion, AnimatePresence } from 'framer-motion';
import Swal from 'sweetalert2';
import './SimpleAudioRecorder.scss';

// Add pulse animation styles
const pulseKeyframes = `
  @keyframes pulse {
    0% {
      opacity: 0.6;
      transform: scale(1);
    }
    50% {
      opacity: 1;
      transform: scale(1.05);
    }
    100% {
      opacity: 0.6;
      transform: scale(1);
    }
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.type = 'text/css';
  styleSheet.innerText = pulseKeyframes;
  document.head.appendChild(styleSheet);
}

interface SimpleAudioRecorderProps {
  onRecordingComplete: (audioBlob: Blob, audioUrl: string) => void;
  onRecordingStart?: () => void;
  onRecordingStop?: () => void;
  onAudioClear?: () => void;
  existingAudioUrl?: string;
  disabled?: boolean;
  className?: string;
}

const SimpleAudioRecorder: React.FC<SimpleAudioRecorderProps> = ({
  onRecordingComplete,
  onRecordingStart,
  onRecordingStop,
  onAudioClear,
  existingAudioUrl,
  disabled = false,
  className = ''
}) => {
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const audioRef = useRef<HTMLAudioElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const waveformRef = useRef<HTMLDivElement>(null);
  
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [hasRecording, setHasRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isModalRecording, setIsModalRecording] = useState(false);
  const [showRecordingModal, setShowRecordingModal] = useState(false);
  const [recordingError, setRecordingError] = useState<string | null>(null);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Wavesurfer configuration
  const { wavesurfer, isPlaying: wavesurferPlaying, currentTime } = useWavesurfer({
    container: waveformRef,
    height: 60,
    waveColor: '#99CAEB',
    progressColor: '#3957A0',
    cursorColor: '#D7A360',
    barWidth: 2,
    barRadius: 3,
    normalize: true,
    url: audioUrl || undefined,
  });

  // Request microphone permission proactively
  const requestMicrophonePermission = async () => {
    try {
      
      // Try to get permission using a minimal getUserMedia call
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: true
      });
      
      // Immediately stop the stream as we only wanted permission
      stream.getTracks().forEach(track => track.stop());
      return true;
      
    } catch (error) {
      return false;
    }
  };

  // Open recording modal
  const openRecordingModal = useCallback(async () => {
    setIsModalRecording(true);
    setShowRecordingModal(true);
    setRecordingError(null);
    
    // First try to get permission proactively
    const hasPermission = await requestMicrophonePermission();
    
    if (!hasPermission) {
      setRecordingError('Microphone access denied. Please:\n\n1. Look for the microphone icon 🎤 in your browser\'s address bar\n2. Click it and select "Allow"\n3. Or go to browser Settings > Privacy & Security > Site Settings > Microphone\n4. Make sure this site is allowed to use microphone\n5. Refresh the page and try again');
      return;
    }
    
    // If permission granted, start recording
    setTimeout(() => {
      startRecording();
    }, 300);
  }, []);

  // Handle modal close
  const handleModalClose = () => {
    if (isRecording) {
      stopRecording();
    }
    setShowRecordingModal(false);
    setIsModalRecording(false);
    setRecordingError(null);
    
    // Clean up stream if there was an error
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };

  // Handle save recording
  const handleSaveRecording = () => {
    if (isRecording) {
      stopRecording();
    }
    setShowRecordingModal(false);
    setIsModalRecording(false);
  };

  // Handle cancel recording
  const handleCancelRecording = () => {
    if (isRecording) {
      stopRecording();
    }
    if (hasRecording) {
      clearRecording();
    }
    setShowRecordingModal(false);
    setIsModalRecording(false);
  };

  // Detect iOS/Safari
  const isIOS = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
           (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  };

  const isSafari = () => {
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  };

  // Check MediaRecorder support
  const isMediaRecorderSupported = () => {
    return typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported;
  };

  // Check microphone permission
  const checkMicrophonePermission = async () => {
    try {
      // Check if Permissions API is supported
      if ('permissions' in navigator) {
        const permission = await navigator.permissions.query({ name: 'microphone' as PermissionName });
        return permission.state;
      }
      return 'prompt'; // Fallback if Permissions API not supported
    } catch (error) {
      return 'prompt'; // Fallback
    }
  };

  // Start recording (iOS compatible)
  const startRecording = useCallback(async () => {
    try {
      
      // Check MediaRecorder support on iOS
      if ((isIOS() || isSafari()) && !isMediaRecorderSupported()) {
        setRecordingError('Recording is not supported on this iOS version. Please try uploading an audio file instead.');
        return;
      }
      
      // Try to request microphone access directly - this may trigger browser permission prompt
      
      // Get user media with iOS-compatible settings
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          // Remove sampleRate for iOS compatibility
          ...(isIOS() ? {} : { sampleRate: 44100 })
        } 
      });
      
      streamRef.current = stream;
      chunksRef.current = [];

      // iOS-compatible MIME type selection
      const mimeTypes = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4',
        'audio/mp4;codecs=mp4a.40.2', // AAC for iOS
        'audio/ogg;codecs=opus'
      ];
      
      // On iOS, prioritize mp4 if webm is not supported
      if (isIOS() || isSafari()) {
        const webmSupported = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') || 
                             MediaRecorder.isTypeSupported('audio/webm');
        if (!webmSupported) {
        }
      }

      let selectedMimeType = '';
      for (const mimeType of mimeTypes) {
        if (MediaRecorder.isTypeSupported(mimeType)) {
          selectedMimeType = mimeType;
          break;
        }
      }


      // Create MediaRecorder
      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: selectedMimeType
      });

      // Handle data available
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      // Handle recording stop
      mediaRecorderRef.current.onstop = () => {
        try {
          
          if (chunksRef.current.length === 0) {
            alert('No audio was recorded. Please try again.');
            return;
          }
          
          const blob = new Blob(chunksRef.current, { type: selectedMimeType || 'audio/webm' });
          
          if (blob.size === 0) {
            if (isIOS()) {
              alert('Recording failed on iOS. This might be due to microphone permissions or unsupported format. Please try uploading an audio file instead.');
            } else {
              alert('Recording failed. Please check your microphone and try again.');
            }
            return;
          }
          
          
          const url = URL.createObjectURL(blob);
          
          // Update state safely with timeout to prevent race conditions
          setTimeout(() => {
            setIsLoadingAudio(true); // Show loader briefly
            setAudioUrl(url);
            setHasRecording(true);
            setIsRecording(false);
            setIsPaused(false);
            setRecordingTime(0);
            
            // Stop loading after 1 second for recordings
            setTimeout(() => {
              setIsLoadingAudio(false);
            }, 1000);
            
            
            // Call parent callbacks with delay
            setTimeout(() => {
              try {
                onRecordingComplete(blob, url);
                onRecordingStop?.();
              } catch (callbackError) {
              }
            }, 100);
            
          }, 50);
          
          // Clean up stream
          if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
          }
          
        } catch (error) {
          alert('Error processing recording. Please try again.');
          
          // Reset state on error
          setIsRecording(false);
          setIsPaused(false);
          setRecordingTime(0);
          
          // Clean up stream
          if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
          }
        }
      };

      // Start recording
      mediaRecorderRef.current.start(100); // Collect 100ms chunks
      setIsRecording(true);
      setIsPaused(false);
      setRecordingTime(0);

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 0.1);
      }, 100);

      onRecordingStart?.();
      
    } catch (error) {
      
      // Enhanced error messages for better debugging
      if (error.name === 'NotAllowedError') {
        setRecordingError('Microphone access denied. Please:\n\n1. Look for the microphone icon 🎤 in your browser\'s address bar\n2. Click it and select "Allow"\n3. Or go to browser Settings > Privacy & Security > Site Settings > Microphone\n4. Make sure this site is allowed to use microphone\n5. Refresh the page and try again');
      } else if (error.name === 'NotFoundError') {
        setRecordingError('No microphone found. Please check your microphone connection.');
      } else if (error.name === 'NotSupportedError') {
        setRecordingError('Recording not supported on this device/browser.');
      } else {
        setRecordingError(`Error accessing microphone: ${error.message || 'Unknown error'}`);
      }
    }
  }, [onRecordingComplete, onRecordingStart, onRecordingStop]);

  // Stop recording
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  }, [isRecording]);

  // Pause recording
  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      if (isPaused) {
        mediaRecorderRef.current.resume();
        setIsPaused(false);
        
        // Resume timer
        timerRef.current = setInterval(() => {
          setRecordingTime(prev => prev + 0.1);
        }, 100);
      } else {
        mediaRecorderRef.current.pause();
        setIsPaused(true);
        
        // Pause timer
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
      }
    }
  }, [isRecording, isPaused]);

  // Play/pause audio using wavesurfer
  const togglePlayback = useCallback(() => {
    if (wavesurfer) {
      wavesurfer.playPause();
    }
  }, [wavesurfer]);

  // Sync isPlaying state with wavesurfer
  useEffect(() => {
    setIsPlaying(wavesurferPlaying);
  }, [wavesurferPlaying]);

  // Handle file upload
  const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      
      const file = event.target.files?.[0];
      if (!file) {
        return;
      }


      // iOS-compatible file type validation
      const validAudioTypes = [
        'audio/',
        'video/mp4', // iOS sometimes uses this for audio
        'video/quicktime', // iOS .mov files
        'application/octet-stream' // Fallback for unknown types
      ];
      
      // More permissive validation for iOS
      const commonAudioExtensions = /\.(mp3|wav|m4a|aac|ogg|webm|mp4|mpeg|mov|caf)$/i;
      const isValidAudio = validAudioTypes.some(type => file.type.startsWith(type)) || 
                          commonAudioExtensions.test(file.name) ||
                          (isIOS() && file.type === ''); // iOS sometimes doesn't set MIME type

      if (!isValidAudio) {
        alert('Please select an audio file (.mp3, .wav, .m4a, .aac, .ogg, .webm)');
        return;
      }

      // Check file size (limit to 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('File is too large. Please select a file smaller than 5MB.');
        return;
      }

      const url = URL.createObjectURL(file);
      
      // Update state safely with delays to prevent race conditions
      setTimeout(() => {
        setIsLoadingAudio(true); // Show loader briefly
        setAudioUrl(url);
        setHasRecording(true);
        setIsRecording(false);
        setIsPaused(false);
        setIsPlaying(false);
        setRecordingTime(0);
        
        // Stop loading after 1 second for file uploads
        setTimeout(() => {
          setIsLoadingAudio(false);
        }, 1000);
        
        
        // Call parent callback with a small delay
        setTimeout(() => {
          try {
            onRecordingComplete(file, url);
          } catch (callbackError) {
          }
        }, 100);
        
      }, 50);
      
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
    } catch (error) {
      alert('Error processing uploaded file. Please try again.');
      
      // Reset file input on error
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [onRecordingComplete]);

  // Clear recording
  const clearRecording = useCallback(async () => {
    
    try {
      const result = await Swal.fire({
        title: 'Clear Audio Recording?',
        text: 'This will permanently delete your current audio recording. You can record or upload a new one after clearing.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Yes, clear it!',
        cancelButtonText: 'Cancel'
      });

      if (result.isConfirmed) {
        
        // Stop any ongoing recording
        if (isRecording) {
          stopRecording();
        }

        // Stop any playing audio
        if (wavesurfer) {
          wavesurfer.stop();
        }
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        }

        // Clean up URL
        if (audioUrl) {
          URL.revokeObjectURL(audioUrl);
        }

        // Reset state
        setAudioUrl(null);
        setHasRecording(false);
        setIsRecording(false);
        setIsPaused(false);
        setIsPlaying(false);
        setRecordingTime(0);

        // Clear timer
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }

        
        // Notify parent component about the clear
        onAudioClear?.();
        
        Swal.fire({
          title: 'Cleared!',
          text: 'Your audio recording has been cleared. You can now record or upload a new one.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });
      }
    } catch (error) {
      Swal.fire({
        title: 'Error',
        text: 'Failed to clear recording. Please try again.',
        icon: 'error'
      });
    }
  }, [isRecording, stopRecording, audioUrl, onAudioClear, wavesurfer]);

  // Download recording
  const downloadRecording = useCallback(() => {
    if (audioUrl) {
      const link = document.createElement('a');
      link.href = audioUrl;
      link.download = `recording-${Date.now()}.webm`;
      link.click();
    }
  }, [audioUrl]);

  // Format time display
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Load existing audio - SIMPLIFIED APPROACH
  useEffect(() => {
    
    if (existingAudioUrl && !hasRecording) {
      // First time loading existing audio - no loading state, just set it directly
      setAudioUrl(existingAudioUrl);
      setHasRecording(true);
      setIsLoadingAudio(false); // Ensure no loading state for existing audio
    }
  }, [existingAudioUrl, hasRecording]);

  // Simple wavesurfer monitoring - removed complex event handling
  useEffect(() => {
    if (wavesurfer && isLoadingAudio) {
      // Simple check: if wavesurfer has duration, stop loading
      const checkReady = () => {
        if (wavesurfer.getDuration() > 0) {
          setIsLoadingAudio(false);
        }
      };
      
      // Check immediately and then periodically
      checkReady();
      const interval = setInterval(checkReady, 500);
      
      return () => clearInterval(interval);
    }
  }, [wavesurfer, isLoadingAudio]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (audioUrl && !existingAudioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl, existingAudioUrl]);

  return (
    <div 
      className={`simple-audio-recorder ${className}`}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Recording Section */}
      {!hasRecording && (
        <div className="recording-section">
          <div className="recording-controls" style={{ marginBottom: '16px' }}>
            {/* Desktop Layout (original) */}
            <div className="d-none d-md-flex" style={{
              gap: '12px',
              alignItems: 'center'
            }}>
              {!isRecording ? (
                <button
                  onClick={openRecordingModal}
                  disabled={disabled}
                  style={{
                    background: '#3957A0',
                    border: 'none',
                    borderRadius: '50px',
                    padding: '12px 24px',
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '14px',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxShadow: '0 4px 15px rgba(57, 87, 160, 0.3)',
                    transition: 'all 0.3s ease',
                    opacity: disabled ? 0.6 : 1
                  }}
                >
                  <FaMicrophone size={14} />
                  <span>Start Recording</span>
                </button>
              ) : null}
              
              <input
                ref={fileInputRef}
                type="file"
                accept={isIOS() ? "audio/*,.mov,.m4a,.aac,.mp3,.wav" : "audio/*"}
                onChange={handleFileUpload}
                style={{ display: 'none' }}
                disabled={disabled}
              />
              
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isRecording || disabled}
                style={{
                  background: 'rgba(108, 117, 125, 0.1)',
                  border: '1px solid rgba(108, 117, 125, 0.2)',
                  borderRadius: '50px',
                  padding: '12px 24px',
                  color: '#6c757d',
                  fontWeight: '600',
                  fontSize: '14px',
                  cursor: (isRecording || disabled) ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  opacity: (isRecording || disabled) ? 0.6 : 1
                }}
              >
                <FaUpload size={14} />
                <span>Upload Audio</span>
              </button>
            </div>

            {/* Mobile Layout (rounded icons) */}
            <div className="d-flex d-md-none" style={{
              justifyContent: 'center',
              alignItems: 'center',
              gap: '20px',
              flexWrap: 'wrap'
            }}>
              {!isRecording ? (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <button
                    onClick={openRecordingModal}
                    disabled={disabled}
                    style={{
                      width: '60px',
                      height: '60px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #3957A0 0%, #4C6BC7 100%)',
                      border: 'none',
                      color: 'white',
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 8px 25px rgba(57, 87, 160, 0.4)',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      opacity: disabled ? 0.6 : 1,
                      transform: 'translateY(0px)',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    <FaMicrophone size={22} />
                    <div style={{
                      position: 'absolute',
                      inset: '-4px',
                      border: '2px solid rgba(57, 87, 160, 0.3)',
                      borderRadius: '50%',
                      animation: 'pulse 2s infinite'
                    }} />
                  </button>
                  <span style={{
                    fontSize: '12px',
                    fontWeight: '500',
                    color: '#3957A0',
                    textAlign: 'center'
                  }}>Record</span>
                </div>
              ) : null}
              
              <input
                ref={fileInputRef}
                type="file"
                accept={isIOS() ? "audio/*,.mov,.m4a,.aac,.mp3,.wav" : "audio/*"}
                onChange={handleFileUpload}
                style={{ display: 'none' }}
                disabled={disabled}
              />
              
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px'
              }}>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isRecording || disabled}
                  style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #6c757d 0%, #7C8A96 100%)',
                    border: 'none',
                    color: 'white',
                    cursor: (isRecording || disabled) ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 8px 25px rgba(108, 117, 125, 0.4)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    opacity: (isRecording || disabled) ? 0.6 : 1,
                    transform: 'translateY(0px)',
                    position: 'relative'
                  }}
                >
                  <FaUpload size={20} />
                </button>
                <span style={{
                  fontSize: '12px',
                  fontWeight: '500',
                  color: '#6c757d',
                  textAlign: 'center'
                }}>Upload</span>
              </div>
            </div>
          </div>

          {/* Recording indicator - only show when not recording in modal */}
          {isRecording && !isModalRecording && (
            <div className="recording-indicator">
              <div className="d-flex align-items-center">
                <div className="recording-dot me-2"></div>
                <span className="recording-time">{formatTime(recordingTime)}</span>
                {isPaused && <span className="ms-2 text-warning">⏸️ Paused</span>}
              </div>
              <div className="recording-wave">
                <div className="wave-bar"></div>
                <div className="wave-bar"></div>
                <div className="wave-bar"></div>
                <div className="wave-bar"></div>
                <div className="wave-bar"></div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Playback Section */}
      {hasRecording && audioUrl && (
        <div className="playback-section">
          <div className="playback-controls" style={{ marginBottom: '16px' }}>
            {/* Desktop Layout */}
            <div className="d-none d-md-flex" style={{
              gap: '12px',
              alignItems: 'center'
            }}>
              <button
                onClick={togglePlayback}
                disabled={isLoadingAudio}
                style={{
                  background: isLoadingAudio
                    ? 'rgba(108, 117, 125, 0.3)'
                    : isPlaying 
                      ? '#D7A360' 
                      : '#3957A0',
                  border: 'none',
                  borderRadius: '50px',
                  padding: '8px 16px',
                  color: 'white',
                  fontWeight: '600',
                  fontSize: '14px',
                  cursor: isLoadingAudio ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 4px 15px rgba(57, 87, 160, 0.3)',
                  transition: 'all 0.3s ease',
                  marginRight: '12px',
                  opacity: isLoadingAudio ? 0.6 : 1
                }}
              >
                {isLoadingAudio ? (
                  <>
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                      style={{
                        width: '14px',
                        height: '14px',
                        border: '2px solid rgba(255, 255, 255, 0.3)',
                        borderTop: '2px solid white',
                        borderRadius: '50%'
                      }}
                    />
                    <span>Loading...</span>
                  </>
                ) : (
                  <>
                    {isPlaying ? <FaPause size={14} /> : <FaPlay size={14} />}
                    <span>{isPlaying ? 'Pause' : 'Play'}</span>
                  </>
                )}
              </button>
              
              <button
                onClick={clearRecording}
                disabled={disabled}
                style={{
                  background: 'rgba(220, 53, 69, 0.1)',
                  border: '1px solid rgba(220, 53, 69, 0.2)',
                  borderRadius: '25px',
                  padding: '8px 16px',
                  color: '#dc3545',
                  fontWeight: '500',
                  fontSize: '13px',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.3s ease',
                  opacity: disabled ? 0.6 : 1
                }}
              >
                <FaTrash size={12} />
                <span>Clear</span>
              </button>
            </div>

            {/* Mobile Layout */}
            <div className="d-flex d-md-none" style={{
              justifyContent: 'center',
              alignItems: 'center',
              gap: '16px',
              flexWrap: 'wrap'
            }}>
              {/* Play/Pause Button */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px'
              }}>
                <button
                  onClick={togglePlayback}
                  disabled={isLoadingAudio}
                  style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    background: isLoadingAudio
                      ? 'linear-gradient(135deg, rgba(108, 117, 125, 0.5) 0%, rgba(108, 117, 125, 0.7) 100%)'
                      : isPlaying 
                        ? 'linear-gradient(135deg, #D7A360 0%, #E6B876 100%)'
                        : 'linear-gradient(135deg, #3957A0 0%, #4C6BC7 100%)',
                    border: 'none',
                    color: 'white',
                    cursor: isLoadingAudio ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: isLoadingAudio
                      ? '0 8px 25px rgba(108, 117, 125, 0.3)'
                      : isPlaying
                        ? '0 8px 25px rgba(215, 163, 96, 0.4)'
                        : '0 8px 25px rgba(57, 87, 160, 0.4)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    opacity: isLoadingAudio ? 0.6 : 1,
                    transform: 'translateY(0px)',
                    position: 'relative'
                  }}
                >
                  {isLoadingAudio ? (
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                      style={{
                        width: '20px',
                        height: '20px',
                        border: '2px solid rgba(255, 255, 255, 0.3)',
                        borderTop: '2px solid white',
                        borderRadius: '50%'
                      }}
                    />
                  ) : (
                    <>
                      {isPlaying ? <FaPause size={18} /> : <FaPlay size={18} />}
                      {!isPlaying && (
                        <div style={{
                          position: 'absolute',
                          inset: '-4px',
                          border: '2px solid rgba(57, 87, 160, 0.3)',
                          borderRadius: '50%',
                          animation: 'pulse 2s infinite'
                        }} />
                      )}
                    </>
                  )}
                </button>
                <span style={{
                  fontSize: '12px',
                  fontWeight: '500',
                  color: isPlaying ? '#D7A360' : '#3957A0',
                  textAlign: 'center'
                }}>
                  {isLoadingAudio ? 'Loading...' : (isPlaying ? 'Pause' : 'Play')}
                </span>
              </div>
              
              {/* Clear Button */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px'
              }}>
                <button
                  onClick={clearRecording}
                  disabled={disabled}
                  style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, rgba(220, 53, 69, 0.1) 0%, rgba(220, 53, 69, 0.2) 100%)',
                    border: '1px solid rgba(220, 53, 69, 0.3)',
                    color: '#dc3545',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    opacity: disabled ? 0.6 : 1,
                    transform: 'translateY(0px)'
                  }}
                >
                  <FaTrash size={14} />
                </button>
                <span style={{
                  fontSize: '12px',
                  fontWeight: '500',
                  color: '#dc3545',
                  textAlign: 'center'
                }}>Clear</span>
              </div>
            </div>
          </div>

          {/* Waveform visualization with loader */}
          <div className="waveform-container">
            {isLoadingAudio ? (
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '60px',
                  background: 'rgba(57, 87, 160, 0.05)',
                  borderRadius: '12px',
                  border: '2px dashed rgba(57, 87, 160, 0.2)'
                }}
              >
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  style={{
                    width: '24px',
                    height: '24px',
                    border: '3px solid rgba(57, 87, 160, 0.2)',
                    borderTop: '3px solid #3957A0',
                    borderRadius: '50%',
                    marginRight: '12px'
                  }}
                />
                <span style={{
                  fontSize: '14px',
                  color: '#3957A0',
                  fontWeight: '500'
                }}>
                  Loading audio...
                </span>
              </motion.div>
            ) : (
              <>
                <div ref={waveformRef} className="waveform"></div>
                {currentTime !== undefined && (
                  <div className="audio-time mt-2">
                    <span className="current-time">{formatTime(currentTime)}</span>
                    {wavesurfer && wavesurfer.getDuration() > 0 && (
                      <span className="total-time"> / {formatTime(wavesurfer.getDuration())}</span>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Recording Modal */}
      <Modal 
        show={showRecordingModal} 
        onHide={handleModalClose}
        backdrop={recordingError ? true : "static"}
        keyboard={recordingError ? true : false}
        centered
      >
        <Modal.Header closeButton={!!recordingError || (!isRecording && !recordingError)}>
          <Modal.Title>🎙️ Recording Audio</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <motion.div 
            className="text-center" 
            style={{ padding: '20px' }}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          >
            <AnimatePresence mode="wait">
              {recordingError ? (
                <motion.div 
                  key="error"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.3 }}
                  style={{
                    padding: '2rem',
                    background: 'linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%)',
                    borderRadius: '16px',
                    border: '2px solid #f5c6cb',
                    textAlign: 'center'
                  }}
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.1, type: "spring", stiffness: 200 }}
                    style={{ color: '#721c24', fontSize: '2rem', marginBottom: '1rem' }}
                  >
                    🚫
                  </motion.div>
                  <motion.div 
                    style={{ 
                      color: '#721c24', 
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      marginBottom: '0.5rem'
                    }}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    Microphone Access Required
                  </motion.div>
                  <motion.div 
                    style={{ 
                      color: '#721c24', 
                      fontSize: '0.9rem',
                      marginBottom: '1.5rem',
                      whiteSpace: 'pre-line', // This will respect \n line breaks
                      textAlign: 'left'
                    }}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    {recordingError}
                  </motion.div>
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                    <motion.button
                      onClick={async () => {
                        setRecordingError(null);
                        const hasPermission = await requestMicrophonePermission();
                        if (hasPermission) {
                          // Permission granted, start recording
                          setTimeout(() => {
                            startRecording();
                          }, 100);
                        } else {
                          // Still denied, show error again
                          setRecordingError('Microphone access denied. Please:\n\n1. Look for the microphone icon 🎤 in your browser\'s address bar\n2. Click it and select "Allow"\n3. Or go to browser Settings > Privacy & Security > Site Settings > Microphone\n4. Make sure this site is allowed to use microphone\n5. Refresh the page and try again');
                        }
                      }}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      style={{
                        background: '#28a745',
                        border: 'none',
                        borderRadius: '50px',
                        padding: '12px 24px',
                        color: 'white',
                        fontWeight: '600',
                        fontSize: '14px',
                        cursor: 'pointer',
                        boxShadow: '0 4px 15px rgba(40, 167, 69, 0.3)',
                        transition: 'all 0.3s ease'
                      }}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                    >
                      🎤 Try Again
                    </motion.button>
                    
                    <motion.button
                      onClick={handleModalClose}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      style={{
                        background: '#dc3545',
                        border: 'none',
                        borderRadius: '50px',
                        padding: '12px 24px',
                        color: 'white',
                        fontWeight: '600',
                        fontSize: '14px',
                        cursor: 'pointer',
                        boxShadow: '0 4px 15px rgba(220, 53, 69, 0.3)',
                        transition: 'all 0.3s ease'
                      }}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 }}
                    >
                      Close
                    </motion.button>
                  </div>
                </motion.div>
              ) : isRecording ? (
                <motion.div 
                  key="recording"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.4 }}
                  style={{
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
                    border: '2px solid #e0e6ff',
                    borderRadius: '16px',
                    padding: '2rem',
                    boxShadow: '0 8px 32px rgba(57, 87, 160, 0.1)'
                  }}
                >
                  <motion.div 
                    className="d-flex align-items-center justify-content-center mb-4"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                  >
                    <motion.div 
                      style={{
                        width: '16px',
                        height: '16px',
                        backgroundColor: '#dc3545',
                        borderRadius: '50%',
                        marginRight: '12px',
                        boxShadow: '0 0 20px rgba(220, 53, 69, 0.5)'
                      }}
                      animate={{ 
                        scale: [1, 1.2, 1],
                        boxShadow: [
                          '0 0 20px rgba(220, 53, 69, 0.5)',
                          '0 0 30px rgba(220, 53, 69, 0.8)',
                          '0 0 20px rgba(220, 53, 69, 0.5)'
                        ]
                      }}
                      transition={{ 
                        duration: 1.5, 
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    />
                    <motion.span 
                      style={{
                        fontFamily: "'Courier New', monospace",
                        fontSize: '1.4rem',
                        fontWeight: 'bold',
                        background: '#3957A0',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                      }}
                      animate={{ scale: [1, 1.05, 1] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      {formatTime(recordingTime)}
                    </motion.span>
                    {/* {isPaused && (
                      <motion.span 
                        className="ms-3 text-warning"
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        style={{ fontSize: '1.1rem', fontWeight: 'bold' }}
                      >
                        ⏸️ Paused
                      </motion.span>
                    )} */}
                  </motion.div>
                  
                  <motion.div 
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      marginTop: '1rem',
                      justifyContent: 'center'
                    }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                  >
                    {[20, 30, 25, 35, 15].map((height, index) => (
                      <motion.div
                        key={index}
                        style={{
                          width: '6px',
                          height: `${height}px`,
                          background: '#3957A0',
                          borderRadius: '3px',
                          boxShadow: '0 2px 8px rgba(57, 87, 160, 0.3)'
                        }}
                        animate={{
                          scaleY: [1, 0.3, 1],
                          opacity: [0.7, 1, 0.7]
                        }}
                        transition={{
                          duration: 1.5,
                          repeat: Infinity,
                          delay: index * 0.1,
                          ease: "easeInOut"
                        }}
                        whileHover={{ scale: 1.1 }}
                      />
                    ))}
                  </motion.div>
                  
                  <motion.div
                    style={{
                      marginTop: '1.5rem',
                      color: '#667eea',
                      fontSize: '0.9rem',
                      fontWeight: '500'
                    }}
                    animate={{ opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    Recording your message...
                  </motion.div>
                  
                  {/* Recording Control Buttons in Modal - Mobile Optimized */}
                  <motion.div
                    style={{ 
                      marginTop: '2rem',
                      display: 'flex',
                      justifyContent: 'center',
                      gap: '20px',
                      flexWrap: 'wrap'
                    }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6, duration: 0.4 }}
                  >
                    {/* Pause/Resume Button */}
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <motion.button
                        onClick={pauseRecording}
                        whileHover={{ scale: 1.08 }}
                        whileTap={{ scale: 0.92 }}
                        style={{
                          width: '70px',
                          height: '70px',
                          borderRadius: '50%',
                          background: isPaused 
                            ? 'linear-gradient(135deg, #28a745 0%, #20c997 100%)'
                            : 'linear-gradient(135deg, #D7A360 0%, #E6B876 100%)',
                          border: 'none',
                          color: 'white',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: isPaused 
                            ? '0 8px 25px rgba(40, 167, 69, 0.4)'
                            : '0 8px 25px rgba(215, 163, 96, 0.4)',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          position: 'relative',
                          overflow: 'hidden'
                        }}
                      >
                        {isPaused ? <BsPlayFill size={24} color='#fff' /> : <BsPauseFill size={24} color='#fff' />}
                        {/* Ripple effect */}
                        <div style={{
                          position: 'absolute',
                          inset: '-3px',
                          border: `2px solid ${isPaused ? 'rgba(40, 167, 69, 0.3)' : 'rgba(215, 163, 96, 0.3)'}`,
                          borderRadius: '50%',
                          animation: 'pulse 2s infinite'
                        }} />
                      </motion.button>
                      <span style={{
                        fontSize: '13px',
                        fontWeight: '600',
                        color: isPaused ? '#28a745' : '#D7A360',
                        textAlign: 'center'
                      }}>{isPaused ? 'Resume' : 'Pause'}</span>
                    </div>
                    
                    {/* Stop & Save Button */}
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <motion.button
                        onClick={() => {
                          stopRecording();
                          // Close the modal after stopping recording
                          setTimeout(() => {
                            setShowRecordingModal(false);
                            setIsModalRecording(false);
                          }, 100);
                        }}
                        whileHover={{ scale: 1.08 }}
                        whileTap={{ scale: 0.92 }}
                        style={{
                          width: '70px',
                          height: '70px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #dc3545 0%, #e74c3c 100%)',
                          border: 'none',
                          color: 'white',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 8px 25px rgba(220, 53, 69, 0.4)',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          position: 'relative',
                          overflow: 'hidden'
                        }}
                      >
                        <FaStop size={20} />
                        {/* Ripple effect */}
                        <div style={{
                          position: 'absolute',
                          inset: '-3px',
                          border: '2px solid rgba(220, 53, 69, 0.3)',
                          borderRadius: '50%',
                          animation: 'pulse 2s infinite 1s'
                        }} />
                      </motion.button>
                      <span style={{
                        fontSize: '13px',
                        fontWeight: '600',
                        color: '#dc3545',
                        textAlign: 'center'
                      }}>Stop & Save</span>
                    </div>
                  </motion.div>
                </motion.div>
              ) : (
                <motion.div 
                  key="waiting"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.3 }}
                  className="text-muted"
                  style={{
                    padding: '2rem',
                    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                    borderRadius: '16px',
                    border: '2px dashed #dee2e6'
                  }}
                >
                  <motion.div
                    animate={{ 
                      rotateY: [0, 10, -10, 0],
                      scale: [1, 1.1, 1]
                    }}
                    transition={{ 
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  >
                    <FaMicrophone size={32} style={{ color: '#3957A0' }} />
                  </motion.div>
                  <motion.div 
                    style={{ marginTop: '1rem', fontSize: '1.1rem' }}
                    animate={{ opacity: [0.7, 1, 0.7] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    Recording will start shortly...
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default SimpleAudioRecorder;